import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { dateFormatter, nestedFieldValueGetter } from 'utils/grid';

import type { TFunction } from 'i18next';
import type { User } from '../../../../../../../../../../types';
import type { GridColDef } from '@mui/x-data-grid-pro';

const useColumnDefinitions = (users: User[]) => {
  const { t } = useTranslation('common');

  return useMemo(() => columnDefinitions(t, users), [t, users]);
};

const columnDefinitions = (t: TFunction, users: User[]) => {
  if (!users) {
    return [];
  }

  const findUser = (row) =>
    users.find((user) => user.accountId === row.accountId);

  return [
    {
      field: t('api_accounts.accountName'),
      headerName: 'Account name',
      valueGetter: (_, row) => findUser(row)?.accountName
    },
    {
      field: 'nameFirstNames',
      headerName: t('firstName'),
      flex: true,
      valueGetter: (_, row) => findUser(row)?.nameFirstNames
    },
    {
      field: 'nameLast',
      headerName: t('lastName'),
      flex: true,
      valueGetter: (_, row) => findUser(row)?.nameLast
    },
    {
      field: 'valid.from',
      type: 'date',
      headerName: t('validFrom'),
      valueGetter: (_, row) => nestedFieldValueGetter('valid.from', row),
      valueFormatter: dateFormatter,
      flex: 1,
      minWidth: 120
    },
    {
      field: 'valid.to',
      type: 'date',
      headerName: t('validTo'),
      valueGetter: (_, row) => nestedFieldValueGetter('valid.to', row),
      valueFormatter: dateFormatter,
      flex: 1,
      minWidth: 120
    }
  ] as GridColDef[];
};

export default useColumnDefinitions;
